<template>
  <div>
    <mayday-editor
      ref="web-article-viewer"
      v-if="toggle"
      v-model="content"
      :editable="false"
      :options="options"
      @open-link-modal="openLinkModal"
      @click:inline-content="handleClickInlineContent"
      @click:attached-file="handleClickAttachedFile"
      @click:image="handleOnImageClick($event.src)"
      @click:focus-hash="handleOnFocusHash"
      @click:copy="handleOnCopy"
      @click:open-link="handleOpenLink"
    />
    <LightBox v-show="!!imageUrl" :image-url.sync="imageUrl" />
    <MaydayEditorOpenLinkModal
      v-if="linkModal.display"
      :display="linkModal.display"
      :src="linkModal.src"
      @close="closeLinkModal"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import MaydayEditorOpenLinkModal from '@/components/Editors/utils/MaydayEditorOpenLinkModal';
import LightBox from '@/components/LightBox';

export default {
  name: 'NodeContentViewer',
  components: {
    MaydayEditorOpenLinkModal,
    LightBox,
  },
  props: {
    body: {
      type: String,
      default: '',
    },
  },
  mounted() {
    this.setEditorContent(this.body);
    setTimeout(() => {
      const hash = this.$route.hash;
      this.handleOnFocusHash(hash);
    }, 1000);
  },
  data() {
    return {
      content: '',
      linkModal: {
        display: false,
        src: '',
      },
      toggle: true,
      imageUrl: '',
      options: {
        fontOptions: true,
        extraFontOptions: true,
        colorOptions: true,
        structurationOptions: true,
        extraStructurationOptions: true,
        tableOptions: true,
        richOptions: true,
        tocClickOptions: false,
        allowToc: true,
      },
    };
  },
  methods: {
    handleOpenLink({ href }) {
      window.open(href, '_blank');
    },
    toggleEditor() {
      this.toggle = false;
      this.$nextTick(() => {
        this.toggle = true;
        this.$nextTick(() => {
          this.setEditorContent(this.body);
        });
      });
    },
    setEditorContent(rawContent) {
      const maydayEditor = this.$refs['web-article-viewer'];
      this.content = rawContent;
      if (maydayEditor) {
        maydayEditor.editor.commands.setContent(this.content);
        this.$nextTick(() => {
          const editorWrapper = maydayEditor.$el.getElementsByClassName(
            'mayday-editor__content__ProseMirror',
          );
          this.$nextTick(() => {
            if (editorWrapper.length > 0) {
              editorWrapper[0].translate = true;
            }
          });
        });
      }
    },
    openLinkModal(src) {
      this.linkModal.display = true;
      this.linkModal.src = src;
    },
    closeLinkModal() {
      this.linkModal.display = false;
      this.linkModal.src = '';
    },
    handleClickInlineContent(content) {
      const items = [
        ...this.previousItems,
        this.focusDiagItem || this.focusNavItem,
      ];

      this.updatePreviousItems(items);
      this.$router.push({
        path: `/web/content/${content.id}`,
      });
    },
    handleClickAttachedFile(evt) {
      if (evt && evt.href) {
        window.open(evt.href, '_blank');
      }
    },
    handleOnImageClick(src) {
      if (src) {
        this.imageUrl = src;
      }
    },
    handleOnFocusHash(hash) {
      if (!hash) return;
      const id = hash.slice(1);

      const uniqueNode = document.querySelector(
        `[data-uid="${id}"], [id="${id}"]`,
      );
      if (!uniqueNode) return;

      uniqueNode.scrollIntoView({ behavior: 'smooth' });
    },
    handleOnCopy(uniqueId) {
      const url = new URL(window.location.href);
      navigator.clipboard.writeText(`${url.origin}${url.pathname}#${uniqueId}`);

      this.$message(this.$t('editor.copy'));
    },
    ...mapActions('webKnowledgeModule', ['updatePreviousItems']),
  },
  computed: {
    ...mapState('webKnowledgeModule', ['previousItems']),
    ...mapGetters('webKnowledgeModule', ['focusNavItem', 'focusDiagItem']),
  },
  watch: {
    body() {
      this.toggleEditor();
    },
  },
};
</script>
<style lang="scss" scoped>
:deep() .mayday-editor {
  padding: 0;

  &__image-wrapper {
    img {
      cursor: pointer;
    }
  }

  &__callout {
    box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  }
}
</style>
